import API_BASE_URL from '../../config/api';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Button from '../../components/base/Button';
import Input from '../../components/base/Input';
import Card from '../../components/base/Card';
import { isSuperAdmin, isAdminOrSuper } from '../../utils/roleHelpers';

// Helper function to generate pagination page numbers with ellipsis
const getPaginationPages = (currentPage: number, totalPages: number, maxVisible: number = 7): (number | string)[] => {
  if (totalPages <= maxVisible) {
    // Show all pages if total is less than max visible
    return Array.from({ length: totalPages }, (_, i) => i + 1);
  }

  const pages: (number | string)[] = [];
  const halfVisible = Math.floor(maxVisible / 2);

  if (currentPage <= halfVisible + 1) {
    // Near the start: show first pages, ellipsis, last page
    for (let i = 1; i <= maxVisible - 2; i++) {
      pages.push(i);
    }
    pages.push('ellipsis');
    pages.push(totalPages);
  } else if (currentPage >= totalPages - halfVisible) {
    // Near the end: show first page, ellipsis, last pages
    pages.push(1);
    pages.push('ellipsis');
    for (let i = totalPages - (maxVisible - 3); i <= totalPages; i++) {
      pages.push(i);
    }
  } else {
    // In the middle: show first, ellipsis, current range, ellipsis, last
    pages.push(1);
    pages.push('ellipsis');
    for (let i = currentPage - 1; i <= currentPage + 1; i++) {
      pages.push(i);
    }
    pages.push('ellipsis');
    pages.push(totalPages);
  }

  return pages;
};

interface ActivityLog {
  id: number;
  created_at: string;
  action_type: string;
  action_details: string;
  parser_name: string | null;
  username: string | null;
  user_id: string | null;
  role: string;
  company_id: string | null;
  pdf_client_name: string | null;
  jobtype: string | null;
  model_tier: string | null;
}

export default function AdminPanel() {
  const navigate = useNavigate();
  const [activityLogs, setActivityLogs] = useState<ActivityLog[]>([]);
  const [isLoadingLogs, setIsLoadingLogs] = useState(false);
  const [logFilters, setLogFilters] = useState({
    company_id: '',
  });
  const [currentAdminCompanyId, setCurrentAdminCompanyId] = useState<string>('');
  const [currentLogPage, setCurrentLogPage] = useState(1);
  const [totalLogs, setTotalLogs] = useState(0);
  const [totalLogPages, setTotalLogPages] = useState(0);
  const LOGS_PER_PAGE = 10;
  const isSuper = isSuperAdmin();

  const handleChangeLogPage = (page: number) => {
    if (page < 1 || (totalLogPages > 0 && page > totalLogPages)) return;
    setCurrentLogPage(page);
    loadActivityLogs(page);
  };

  // Check if user has access
  useEffect(() => {
    if (!isAdminOrSuper()) {
      navigate('/home');
      return;
    }
  }, [navigate]);

  // Load current admin's company_id from token
  useEffect(() => {
    const loadCurrentAdminCompanyId = () => {
      try {
        const token = localStorage.getItem('token') || '';
        if (token && token.startsWith('token-')) {
          const tokenParts = token.substring(6).split('-'); // Remove 'token-' prefix and split
          if (tokenParts.length >= 2) {
            // Company ID is everything after the username
            const extractedCompanyId = tokenParts.slice(1).join('-'); // Join in case company_id contains hyphens
            setCurrentAdminCompanyId(extractedCompanyId);
            return;
          }
        }
        
        // Fallback: try to get from API if token doesn't have company_id
        const loadFromAPI = async () => {
          try {
            const response = await fetch(`${API_BASE_URL}/user`, {
              headers: {
                'Authorization': `Bearer ${token}`,
              },
            });
            const data = await response.json();
            if (data.success && data.company_id) {
              setCurrentAdminCompanyId(data.company_id);
            }
          } catch (error) {
            console.error('Failed to load company ID from API:', error);
          }
        };
        loadFromAPI();
      } catch (error) {
        console.error('Failed to extract company ID from token:', error);
      }
    };
    loadCurrentAdminCompanyId();
  }, []);

  // Load activity logs on component mount
  useEffect(() => {
    loadActivityLogs(1);
  }, []);

  const loadActivityLogs = async (page: number = currentLogPage) => {
    setIsLoadingLogs(true);
    try {
      const params = new URLSearchParams();
      // For super_admin: use filter if provided, otherwise show all
      // For regular admin: backend will automatically filter by their company_id
      if (isSuper && logFilters.company_id) {
        params.append('company_id', logFilters.company_id);
      }
      
      // Add pagination parameters
      params.append('page', page.toString());
      params.append('page_size', LOGS_PER_PAGE.toString());
      
      const response = await fetch(`${API_BASE_URL}/admin/activity-logs?${params.toString()}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token') || ''}`,
        },
      });
      
      const data = await response.json();
      if (data.success && Array.isArray(data.logs)) {
        setActivityLogs(data.logs);
        setTotalLogs(data.total || 0);
        setTotalLogPages(data.total_pages || 0);
        setCurrentLogPage(data.page || page);
      } else {
        console.error('Failed to load activity logs:', data.error || data);
        setActivityLogs([]);
        setTotalLogs(0);
        setTotalLogPages(0);
        setCurrentLogPage(1);
      }
    } catch (e) {
      console.error('Failed to load activity logs', e);
      setActivityLogs([]);
      setTotalLogs(0);
      setTotalLogPages(0);
    } finally {
      setIsLoadingLogs(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-cyan-50">
      <Navbar showUserActions={true} />
      
      <div className="flex flex-col items-center px-4 py-8">
        <div className="w-full max-w-7xl">
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="text-4xl font-bold text-gray-800 mb-2">
                {isSuper ? 'Activity Logs' : 'Activity Logs'}
              </h1>
              <div className="w-full h-px bg-gray-300"></div>
            </div>
            <Button variant="outline" onClick={() => navigate('/home')}>
              <i className="ri-arrow-left-line mr-2"></i>
              Back to Home
            </Button>
          </div>

          {/* Activity Logs Section */}
          <Card className="min-h-[400px]">
            <div className="mb-4">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-2xl font-semibold text-gray-800">Activity Logs</h2>
                <Button onClick={() => { setCurrentLogPage(1); loadActivityLogs(1); }} disabled={isLoadingLogs} className="whitespace-nowrap">
                  {isLoadingLogs ? (
                    <>
                      <i className="ri-loader-line animate-spin mr-2"></i>
                      Loading...
                    </>
                  ) : (
                    <>
                      <i className="ri-refresh-line mr-2"></i>
                      Refresh
                    </>
                  )}
                </Button>
              </div>
              <div className="flex gap-4 items-end mb-4">
                {isSuper ? (
                  <div className="flex-1">
                    <Input
                      type="text"
                      placeholder="Filter by company ID (leave empty to see all logs)"
                      value={logFilters.company_id}
                      onChange={(e) => setLogFilters({ ...logFilters, company_id: e.target.value })}
                      label="Company ID (Optional - leave empty to see all)"
                    />
                    {!logFilters.company_id && (
                      <p className="text-xs text-gray-500 mt-1">
                        Showing all activity logs from all admins and users
                      </p>
                    )}
                  </div>
                ) : (
                  <div className="flex-1">
                    <p className="text-sm text-gray-600">
                      Showing activity logs for your company: <span className="font-semibold">{currentAdminCompanyId.toUpperCase()}</span>
                    </p>
                  </div>
                )}
                {isSuper && (
                  <Button onClick={() => { setCurrentLogPage(1); loadActivityLogs(1); }} disabled={isLoadingLogs} className="whitespace-nowrap">
                    {isLoadingLogs ? (
                      <>
                        <i className="ri-loader-line animate-spin mr-2"></i>
                        Loading...
                      </>
                    ) : (
                      <>
                        <i className="ri-filter-line mr-2"></i>
                        Filter
                      </>
                    )}
                  </Button>
                )}
              </div>
            </div>
            <div className="overflow-x-auto overflow-y-auto max-h-[600px] border border-gray-200 rounded-lg">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-200">
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Timestamp</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Role</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">User</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Company</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Action</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Parser</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Model Tier</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {isLoadingLogs ? (
                    <tr>
                      <td colSpan={7} className="px-4 py-8 text-center text-gray-500">
                        <i className="ri-loader-line animate-spin mr-2"></i>
                        Loading logs...
                      </td>
                    </tr>
                  ) : activityLogs.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="px-4 py-8 text-center text-gray-500">
                        <i className="ri-file-list-3-line text-2xl mb-2 block"></i>
                        No activity logs found.
                      </td>
                    </tr>
                  ) : (
                    activityLogs.map((log) => (
                      <tr key={log.id} className="hover:bg-gray-50">
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">
                          {log.created_at ? new Date(log.created_at).toLocaleString('en-IN', { 
                            timeZone: 'Asia/Kolkata',
                            year: 'numeric',
                            month: '2-digit',
                            day: '2-digit',
                            hour: '2-digit',
                            minute: '2-digit',
                            second: '2-digit',
                            hour12: true
                          }) : '-'}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            log.role === 'super_admin' ? 'bg-red-100 text-red-800' :
                            log.role === 'admin' ? 'bg-purple-100 text-purple-800' : 'bg-blue-100 text-blue-800'
                          }`}>
                            {log.role || 'user'}
                          </span>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <div className="text-sm">
                            <div className="font-medium text-gray-900">{log.username || log.user_id || '-'}</div>
                            {log.pdf_client_name && (
                              <div className="text-gray-500 text-xs mt-1">{log.pdf_client_name}</div>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">
                          {log.company_id ? log.company_id.toUpperCase() : '-'}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <span className={`px-2 py-1 rounded text-xs font-medium ${
                            log.action_type === 'login' ? 'bg-green-100 text-green-800' :
                            log.action_type === 'logout' ? 'bg-blue-100 text-blue-800' :
                            log.action_type === 'parser_created' ? 'bg-purple-100 text-purple-800' :
                            log.action_type === 'parser_modified' ? 'bg-yellow-100 text-yellow-800' :
                            log.action_type === 'parser_tested' ? 'bg-indigo-100 text-indigo-800' :
                            log.action_type === 'user_created' ? 'bg-teal-100 text-teal-800' :
                            log.action_type === 'user_deleted' ? 'bg-red-100 text-red-800' :
                            log.action_type === 'user_status_changed' ? 'bg-orange-100 text-orange-800' :
                            log.action_type === 'model_tier_changed' ? 'bg-cyan-100 text-cyan-800' :
                            'bg-gray-100 text-gray-800'
                          }`}>
                            {(log.action_type || 'unknown').replace(/_/g, ' ')}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600">
                          {log.parser_name || '-'}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600">
                          {log.parser_name ? (log.model_tier || 'Standard') : '-'}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
            {totalLogs > LOGS_PER_PAGE && (
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between px-4 py-3 border-t border-gray-200 bg-white gap-2">
                <p className="text-sm text-gray-600">
                  Showing{' '}
                  <span className="font-medium">{(currentLogPage - 1) * LOGS_PER_PAGE + 1}</span>
                  {'–'}
                  <span className="font-medium">
                    {Math.min(currentLogPage * LOGS_PER_PAGE, totalLogs)}
                  </span>{' '}
                  of <span className="font-medium">{totalLogs}</span> activities
                </p>
                <div className="flex items-center justify-end space-x-1">
                  <button
                    onClick={() => handleChangeLogPage(currentLogPage - 1)}
                    disabled={currentLogPage === 1}
                    className="px-2 py-1 text-sm border rounded-md bg-white text-gray-700 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
                  >
                    <i className="ri-arrow-left-s-line" />
                    <span className="hidden sm:inline">Prev</span>
                  </button>
                  {getPaginationPages(currentLogPage, totalLogPages).map((page, idx) => {
                    if (page === 'ellipsis') {
                      return (
                        <span key={`ellipsis-${idx}`} className="px-2 py-1 text-sm text-gray-500">
                          ...
                        </span>
                      );
                    }
                    const pageNum = page as number;
                    return (
                      <button
                        key={pageNum}
                        onClick={() => handleChangeLogPage(pageNum)}
                        className={`px-2.5 py-1 text-sm rounded-md border ${
                          pageNum === currentLogPage
                            ? 'bg-indigo-600 text-white border-indigo-600'
                            : 'bg-white text-gray-700 hover:bg-gray-100'
                        }`}
                      >
                        {pageNum}
                      </button>
                    );
                  })}
                  <button
                    onClick={() => handleChangeLogPage(currentLogPage + 1)}
                    disabled={currentLogPage === totalLogPages}
                    className="px-2 py-1 text-sm border rounded-md bg-white text-gray-700 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
                  >
                    <span className="hidden sm:inline">Next</span>
                    <i className="ri-arrow-right-s-line" />
                  </button>
                </div>
              </div>
            )}
          </Card>
        </div>
      </div>
    </div>
  );
}

